/* eslint-disable react-native/no-unused-styles */

import React, { type ComponentProps } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';

import { useResponsive } from '../../../../hooks';
import { Icon } from '../../../Icon';
import { BodyText } from '../../../Text';
import {
  ICON_SIZE_MD,
  ICON_SIZE_SM,
  ICON_SIZE_XS,
  QUANTITY_STEPPER_SIZE_MD,
  QUANTITY_STEPPER_SIZE_SM,
  QUANTITY_STEPPER_SIZE_XS,
} from '../../IngredientCardV2.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardQuantity = (props: Props) => {
  const { children, iconName = 'IconCheck2', size, shouldUseIcon } = props;

  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const selectedSize = size ?? match(['xs', 'sm']);
  const iconSize = props.iconSize ?? ICON_SIZES[selectedSize];
  const allQuantityContainerStyles = [styles.quantityContainer, quantityContainerSizeStyles[selectedSize]]; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container} pointerEvents="none">
      <View style={allQuantityContainerStyles}>
        {shouldUseIcon ? (
          <Icon name={iconName} width={iconSize} height={iconSize} color={theme.colors.KALE} />
        ) : (
          <BodyText
            sizeMatch={size === 'md' ? ['30'] : ['22']}
            maxFontSizeMultiplier={1.2}
            style={styles.quantityText}
          >
            {children}
          </BodyText>
        )}
      </View>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZES: Record<Size, number> = {
  xs: ICON_SIZE_XS,
  sm: ICON_SIZE_SM,
  md: ICON_SIZE_MD,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.LIME,
    borderColor: theme.colors.KALE,
    borderWidth: 1,
  },
  quantityText: {
    textAlign: 'center',
    color: theme.colors.KALE,
  },
});

const quantityContainerSizeStyles = StyleSheet.create<Record<Size, ViewStyle>>({
  xs: {
    width: QUANTITY_STEPPER_SIZE_XS,
    height: QUANTITY_STEPPER_SIZE_XS,
    borderRadius: QUANTITY_STEPPER_SIZE_XS,
  },
  sm: {
    width: QUANTITY_STEPPER_SIZE_SM,
    height: QUANTITY_STEPPER_SIZE_SM,
    borderRadius: QUANTITY_STEPPER_SIZE_SM,
  },
  md: {
    width: QUANTITY_STEPPER_SIZE_MD,
    height: QUANTITY_STEPPER_SIZE_MD,
    borderRadius: QUANTITY_STEPPER_SIZE_MD,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  children?: string | number;
  iconName?: ComponentProps<typeof Icon>['name'];
  shouldUseIcon?: boolean;
  iconSize?: number;
  size?: Size;
};

type Size = 'xs' | 'sm' | 'md';
